import { useEffect, useState } from 'react';
import { useUIStore } from '@/state/store';
import { apiFetch } from '@/lib/auth';

/**
 * Push-Subscription-Flow:
 *  1. Notification-Permission anfordern (nur wenn Status === 'available')
 *  2. VAPID-Public-Key vom Backend
 *  3. PushManager.subscribe
 *  4. Subscription an /api/push/subscribe POSTen mit Dev-Token
 *
 * Triggert auch auf 'bamoa:logged-in', damit nach frischem Login (ohne
 * Page-Reload) sofort eine Subscription gemacht wird, sobald Token da ist.
 */
export function usePushSubscription() {
  const status = useUIStore((s) => s.status);
  const [loginTick, setLoginTick] = useState(0);

  useEffect(() => {
    const onLogin = () => setLoginTick((t) => t + 1);
    window.addEventListener('bamoa:logged-in', onLogin);
    return () => window.removeEventListener('bamoa:logged-in', onLogin);
  }, []);

  useEffect(() => {
    if (status !== 'available') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    let cancelled = false;

    async function setup() {
      try {
        if (Notification.permission === 'default') {
          const p = await Notification.requestPermission();
          if (p !== 'granted') return;
        }
        if (Notification.permission !== 'granted') return;

        const reg = await navigator.serviceWorker.ready;
        if (cancelled) return;

        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          const keyRes = await fetch('/api/push/vapid-public-key');
          if (!keyRes.ok) {
            console.warn('[push] VAPID-Key konnte nicht geholt werden');
            return;
          }
          const { key } = await keyRes.json();
          if (!key) {
            console.warn('[push] VAPID-Key leer');
            return;
          }
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(key),
          });
        }
        if (cancelled) return;

        const json = sub.toJSON();
        if (!json.keys?.p256dh || !json.keys?.auth || !json.endpoint) return;

        const res = await apiFetch('/api/push/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            endpoint: json.endpoint,
            keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
            userAgent: navigator.userAgent,
            capabilities: {
              pwaInstalled: window.matchMedia('(display-mode: standalone)').matches,
              userAgent: navigator.userAgent,
            },
          }),
        });
        if (!res.ok) {
          console.warn(`[push] Subscribe-Backend antwortete mit ${res.status}`);
          return;
        }

        console.log('[push] Subscription beim Backend registriert');
      } catch (e) {
        console.warn('[push] Setup-Fehler:', e);
      }
    }

    setup();
    return () => {
      cancelled = true;
    };
  }, [status, loginTick]);
}

function urlBase64ToUint8Array(base64: string) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
